/**
 * The commissioning screen (M6 plan P34, spec §7.2, §8): buy a charter for a system, then pick one of its approved
 * sites, a station type and a level, and commission it. The site is picked from a list (where it sits and its
 * distance from the system's star) rather than on the system map: the map has no site layer and the list is
 * keyboard and pad friendly (D-414). Thin: the rules are `stationsModel.ts`.
 */
import type { ReactNode } from 'react'
import type { CommissionSystemView, CommissionView, CorpFactionId } from '../../strategic/types'
import type { WareId } from '../../strategic/wares'
import { FocusButton } from '../focus'
import { formatCr } from '../format'
import { wareName } from '../strategic/names'
import { anchorLabel, charterModel, commissionModel, freeSites, typeLabel, type CommissionDraft } from './stationsModel'

export interface CommissionProps {
  view: CommissionView
  draft: CommissionDraft
  busy: boolean
  status: string | null
  systemName(id: string): string
  holderName(holder: CorpFactionId | null): string
  /** The Career's standing with a system's holder. */
  standing(system: CommissionSystemView): number
  onBack(): void
  onBuyCharter(systemId: string): void
  onSystem(systemId: string): void
  onSite(index: number): void
  onType(delta: 1 | -1): void
  onLevel(delta: 1 | -1): void
  onAutoBuy(): void
  onCommission(): void
}

function km(pos: readonly [number, number, number]): string {
  return `${Math.round(Math.hypot(pos[0], pos[1], pos[2]) / 1000).toLocaleString('en-US')} km out`
}

function costText(cost: Partial<Record<WareId, number>>): string {
  return (Object.keys(cost) as WareId[]).map((w) => `${cost[w] ?? 0} t ${wareName(w)}`).join(', ')
}

export function Commission(p: CommissionProps): ReactNode {
  const v = p.view
  const d = p.draft
  const m = commissionModel(v, d)
  const selected = v.systems.find((s) => s.systemId === d.systemId)
  let i = 0
  return (
    <div className="sui fp-panel corp-panel" data-testid="commission-screen">
      <div className="corp-head">
        <FocusButton group="corp" index={i++} className="ui-btn--small" testId="commission-back" onActivate={p.onBack}>
          ← Stations
        </FocusButton>
        <h3 className="corp-title">Charters and commissioning</h3>
      </div>
      <p className="fp-meta">
        A charter costs {formatCr(v.charterPrice)} from the company account (treasury {formatCr(v.treasury)}). Up to {v.maxPerSystem} per system; {v.used} of{' '}
        {v.capacity} used. A system held by another faction needs your standing with it at {v.minStanding}.
      </p>

      <h4 className="corp-sub">Systems</h4>
      <ul className="corp-list" data-testid="commission-systems">
        {v.systems.map((s) => {
          const c = charterModel(v, s, p.standing(s))
          const free = freeSites(s).length
          return (
            <li key={s.systemId} className={`corp-row${s.systemId === d.systemId ? ' corp-row--on' : ''}`} data-testid={`commission-system-${s.systemId}`}>
              <span>
                {p.systemName(s.systemId)}
                <span className="fp-row__sub">
                  {' '}
                  {p.holderName(s.holder)} · {free} free sites · {s.stations} stations · {s.charters} charters
                </span>
              </span>
              <FocusButton
                group="corp"
                index={i++}
                className="ui-btn--small"
                testId={`commission-buy-${s.systemId}`}
                disabled={p.busy || !c.canBuy}
                title={c.canBuy ? undefined : c.reason}
                onActivate={() => p.onBuyCharter(s.systemId)}
              >
                Buy charter
              </FocusButton>
              <FocusButton
                group="corp"
                index={i++}
                className="ui-btn--small"
                testId={`commission-pick-${s.systemId}`}
                disabled={s.charters === 0}
                title={s.charters === 0 ? 'Buy a charter here first.' : undefined}
                onActivate={() => p.onSystem(s.systemId)}
              >
                {s.systemId === d.systemId ? 'Selected' : 'Select'}
              </FocusButton>
            </li>
          )
        })}
      </ul>

      {selected !== undefined && selected.charters > 0 ? (
        <>
          <h4 className="corp-sub">Site in {p.systemName(selected.systemId)}</h4>
          {m.sites.length === 0 ? (
            <p className="sui-empty">Every approved site here is taken.</p>
          ) : (
            <ul className="corp-list" data-testid="commission-sites">
              {m.sites.map((s) => (
                <li key={s.index}>
                  <FocusButton
                    group="corp"
                    index={i++}
                    className={`ui-btn--small${s.index === d.site ? ' ui-btn--on' : ''}`}
                    testId={`commission-site-${s.index}`}
                    onActivate={() => p.onSite(s.index)}
                  >
                    {s.index === d.site ? '● ' : '○ '}Site {s.index + 1}: {anchorLabel(s.anchor)}, {km(s.pos)}
                  </FocusButton>
                </li>
              ))}
            </ul>
          )}
          <h4 className="corp-sub">Type and level</h4>
          <div className="corp-fields">
            <FocusButton group="corp" index={i++} className="ui-btn--small" testId="commission-type" onActivate={() => p.onType(1)} onAdjust={p.onType}>
              Type ◂ {typeLabel(d.type)} ▸
            </FocusButton>
            <FocusButton group="corp" index={i++} className="ui-btn--small" testId="commission-level" onActivate={() => p.onLevel(1)} onAdjust={p.onLevel}>
              Level ◂ {d.level} ▸
            </FocusButton>
            <FocusButton group="corp" index={i++} className="ui-btn--small" testId="commission-autobuy" onActivate={p.onAutoBuy}>
              Auto-buy wares: {d.autoBuy ? 'on' : 'off'}
            </FocusButton>
          </div>
          {m.level !== null ? (
            <p className="fp-meta" data-testid="commission-cost">
              Needs {costText(m.level.cost)}; builds in {m.level.days} days once supplied.{' '}
              {d.autoBuy ? 'Wares are bought from the company account as funds allow.' : 'Deliver the wares yourself or with supply orders.'}
            </p>
          ) : null}
          <FocusButton
            group="corp"
            index={i++}
            testId="commission-go"
            disabled={p.busy || !m.canCommission}
            title={m.canCommission ? undefined : m.reason}
            onActivate={p.onCommission}
          >
            Commission the {typeLabel(d.type).toLowerCase()}
          </FocusButton>
          {!m.canCommission ? <p className="fp-meta">{m.reason}</p> : null}
        </>
      ) : null}
      {p.status !== null ? (
        <p className="fp-status" role="status" data-testid="commission-status">
          {p.status}
        </p>
      ) : null}
    </div>
  )
}
